import { AnyAction } from "redux";
import { delay, put, takeLatest, call, select } from "redux-saga/effects";
import { ISOFILEMANAGMENT } from "./actions";
import {
  deleteList,
  fetchFileList,
  getFileList,
  pageDetails,
  uploadLoader,
} from "./iso-file-management.action";
import {
  hideLoader,
  showLoader,
  showToast,
} from "../../store/model/model.action";
import { SagaIterator } from "redux-saga";
import {
  cancelUploadIsoFile,
  deleteIsoList,
  downloadIsoFile,
  downloadLargeFileWithFetch,
  getIsoList,
  uploadIsoFile,
  uploadIsoFileUpload,
} from "../store/iso-file-management.service";
import { UPLOADISO } from "../../../../../common/apiEndPoint/apiEndPoint";
import { getFormattedDateTime } from "../../../../../common/utility/dataManagementUtils";
import { link } from "fs/promises";
import { sha256HashFile } from "../../../../../common/utility/shaHash";
import { store } from "../../../../../store";
import { resetAbortController } from "../../common/interceptor/base.services";
const chunkSize = 5 * 1024 * 1024;
export function* IsoFileManagementListSaga(action: AnyAction): SagaIterator {
  try {
    if (!action?.payload?.status) {
      yield put(showLoader());
    }
    const pageNumber = action?.payload?.pageNumber || 1;
    const response = (yield call(getIsoList, pageNumber)) as {
      status: number;
      data: {
        isoModels: any;
        pageNumber: number;
        totalPages: number;
        totalRecords: number;
      };
    };
    yield put(hideLoader());
    if (response.status === 200) {
      const { isoModels, pageNumber, totalPages, totalRecords } = response.data;
      yield put(getFileList(isoModels, action?.payload?.status));
      yield put(pageDetails(pageNumber, totalPages, totalRecords));
    }
  } catch (error) {
    yield put(hideLoader());
  }
}

function formatSize(bytes: number): string {
  const MB = 1024 * 1024;
  const GB = MB * 1024;

  if (bytes >= GB) {
    return `${(bytes / GB).toFixed(2)} GB`;
  } else {
    return `${(bytes / MB).toFixed(2)} MB`;
  }
}

export function* IsoFileUploadedSaga(action: AnyAction): SagaIterator {
  try {
    if (!action || !action.payload) {
      yield put(hideLoader());
      yield put(
        showToast({
          type: "error",
          content: "Invalid upload payload.",
        })
      );
      return;
    }
    const { size, file, name, defaultChunkSize  = 1 * 1024 * 1024, catalog, description, imageType } = action.payload;
    const effectiveChunkSize = size < defaultChunkSize  ? size : defaultChunkSize ;
   
    const validationChunk = file.slice(0, effectiveChunkSize);
    const chunkArrayBuffer = yield validationChunk.arrayBuffer();
    const chunkBlob = new Blob([chunkArrayBuffer], { type: "application/octet-stream" });
   
    const totalChunks = size % effectiveChunkSize === 0
      ? size / effectiveChunkSize
      : Math.floor(size / effectiveChunkSize) + 1;
 
    const formData = new FormData();
    formData.append("FormFile", chunkBlob, name);
    formData.append("Name", name);
    formData.append("Catalog[Id]", catalog.id);
    formData.append("Size", size.toString());
    formData.append("Description", description);
    formData.append("ImageType", imageType);
    const response = yield call(uploadIsoFile, formData);
    yield put(hideLoader());

    if (response && response.status === 200) {
      const { uploadUrl, vcdToken, mediaId, jobId } = response?.data;

      const { size, file, name } = action.payload;
       yield put(uploadLoader("0.01", formatSize(0), formatSize(size), name));
      yield put({
        type: "SET_UPLOAD_PROGRESS_MEDIA_ID",
        payload: jobId || "null",
      });

      const totalChunks =
        size % chunkSize === 0
          ? size / chunkSize
          : Math.floor(size / chunkSize) + 1;

      for (let currentChunk = 1; currentChunk <= totalChunks; currentChunk++) {
        const startOfChunk = (currentChunk - 1) * chunkSize;
        const endOfChunk = Math.min(startOfChunk + chunkSize, size);

        try {
          const uploadMediaId = yield select(
            (state) => state.isoFileManagementReducer.uploadMediaId
          );
          if (!uploadMediaId) {
            let zero = "0";
            yield put(uploadLoader(zero, zero, zero, ""));
            break;
          }
    

          const chunk = file.slice(startOfChunk, endOfChunk);
          const chunkArrayBuffer = yield chunk.arrayBuffer();
          const chunkBlob = new Blob([chunkArrayBuffer], {
            type: "application/octet-stream",
          });

          const payload = {
            uploadUrl,
            contentRange: `bytes ${startOfChunk}-${endOfChunk - 1}/${size}`,
            authorization: vcdToken,
            chunkBlob,
          };

          // Upload chunk
          const uploadResponse = yield call(uploadIsoFileUpload, payload);

          if (uploadResponse.status !== 200 && uploadResponse.status !== 201) {
            yield put(uploadLoader("0", "0", "0", ""));
            yield put(
              showToast({
                type: "error",
                content: "Unable to perform this action. Please try again.",
              })
            );
            break;
          }

          // Calculate progress
          const uploadedBytes = endOfChunk;
          const percentageValue = (uploadedBytes / size) * 100;
          const loadedSize = formatSize(uploadedBytes);
          const totalSize = formatSize(size);

          // Show progress for all chunks including the last one
          if (currentChunk !== totalChunks) {
            yield put(
              uploadLoader(
                percentageValue.toFixed(2),
                loadedSize,
                totalSize,
                name
              )
            );
          }

          // After last chunk completes, reset and show success
          if (currentChunk === totalChunks) {
            yield put(uploadLoader("0", "0", "0", ""));
            yield put(
              showToast({ type: "success", content: `${name} Uploaded!` })
            );
          }
        } catch (error) {
          yield put(uploadLoader("0", "0", "0", ""));
          yield put(
            showToast({
              type: "error",
              content: "Unable to perform this action. Please try again.",
            })
          );
          break;
        }
      }
    } else {
      showToast({
        type: "error",
        content: "Unable to perform this action. Please try again.",
      });
    }
  } catch (error) {
    yield put(hideLoader());
    yield put(uploadLoader("0", "0", "0", ""));
    if (error && typeof error === "object" && "response" in error) {
      const apiError = error.response as {
        data?: {
          title?: string;
          message?: string;
          details?: { message?: string };
          error?: string;
        };
      };
      const { data } = apiError;
      const errorMessage =
        data?.details?.message ||
        data?.message ||
        data?.error ||
        "An unexpected error occurred";
      yield put(showToast({ type: "error", content: errorMessage }));
    } else {
      yield put(
        showToast({ type: "error", content: "An unexpected error occurred" })
      );
    }
  }
}

export function* IsoFileMultipleDeletedSaga(action: AnyAction): SagaIterator {
  try {
    yield put(showLoader());
    let payload = {
      mediaIds: action.payload,
    };
    const response = yield call(deleteIsoList, payload);
    if (response && response.status === 200) {
      yield put(hideLoader());
      const { message, status, totalRequested, totalDeleted, totalSkipped } =
        response?.data;

      if (status === "failure") {
        yield put(
          showToast({
            type: "information",
            content:
              `One or more of the selected ISO file(s) could not be deleted because they are currently in use.`,
          })
        );
      } else if (status === "success") {
        yield put(
          showToast({
            type: "success",
            content: `ISO ${
              action.payload?.length > 1 ? "files" : "file"
            } deleted successfully.`,
          })
        );
      } else {
        yield put(
          showToast({
            type: "warning",
            content: `One or more of the selected ISO file(s) could not be deleted because they are currently in use.`,
          })
        );
      }

      yield put(fetchFileList(1, true));
    }
  } catch (error) {
    yield put(hideLoader());
    yield put(
      showToast({
        type: "error",
        content: `ISO ${
          action.payload?.length > 1 ? "files" : "file"
        } deleted failed.`,
      })
    );
  }
}

export const downloadFileFromUrl = async (
  downloadUrl: string,
  fileName: string
) => {
  try {
    const response = await fetch(downloadUrl);
    const blob = await response.blob();

    const urlObject = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = urlObject;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    setTimeout(() => URL.revokeObjectURL(urlObject), 60 * 1000);
  } catch (error) {
    console.error("Download failed", error);
  }
};

export function* fetchDownloadListSaga(
  action: AnyAction
): Generator<any, void, unknown> {
  try {
    yield put(showLoader());
    let payload = {
      mediaId: action?.payload?.mediaId,
      name: action?.payload?.name,
    };

    const response = (yield call(downloadIsoFile, payload)) as {
      status: number;
      data: any;
      headers: any;
    };
    if (response && response.status === 200) {
      const blob = response.data;
      const { downloadUrl } = blob;
      const fileName = action?.payload?.name;
      yield put(hideLoader());
      yield call(downloadLargeFileWithFetch, downloadUrl, fileName);
      yield put(
        showToast({
          type: "info",
          content: `File download started. Please wait for it to complete`,
        })
      );
    } else {
      const blob = response.data;
      const { message } = blob;
      yield put(hideLoader());
      yield put(
        showToast({
          type: "error",
          content: message || `Download failed. Please try again.`,
        })
      );
    }
  } catch (error) {
    yield put(hideLoader());
  }
}
export function* cancelUploadSaga(
  action: AnyAction
): Generator<any, void, unknown> {
  try {
    yield put(showLoader());
    const uploadMediaId = yield select(
      (state) => state.isoFileManagementReducer.uploadMediaId
    );

    // First, reset the upload media ID to stop any ongoing chunk uploads
    yield put({ type: "SET_UPLOAD_PROGRESS_MEDIA_ID", payload: null });

    // Reset abort controller to cancel all ongoing API requests
     resetAbortController();

    let zero = "0";
    yield put(uploadLoader(zero, zero, zero, ""));

    // Now call the cancel API
    let payload = {
      mediaId: uploadMediaId,
    };

    const response = (yield call(cancelUploadIsoFile, payload)) as {
      status: number;
      data: any;
      headers: any;
    };
  yield put(
        showToast({
          type: "info",
          content: "Upload Cancellation triggered.",
        })
      );
    yield put(hideLoader());
  } catch (error) {
    let zero = "0";
    yield put(uploadLoader(zero, zero, zero, ""));
    yield put({ type: "SET_UPLOAD_PROGRESS_MEDIA_ID", payload: null });
    resetAbortController();
    yield put(hideLoader());
  }
}

export default function* watchAnalyticsLoad(): Generator<any, void, unknown> {
  yield takeLatest(
    ISOFILEMANAGMENT.FETCH_MANAGEMENT_LIST,
    IsoFileManagementListSaga
  );
  yield takeLatest(ISOFILEMANAGMENT.UPLOAD_FILE, IsoFileUploadedSaga);
  yield takeLatest(
    ISOFILEMANAGMENT.DELETE_MULTIPLE_LIST,
    IsoFileMultipleDeletedSaga
  );
  yield takeLatest(ISOFILEMANAGMENT.FETCH_DOWNLOAD_LIST, fetchDownloadListSaga);
  yield takeLatest(ISOFILEMANAGMENT.CANCEL_ISO_UPLOAD, cancelUploadSaga);
}
